import { createSlice, type PayloadAction } from '@reduxjs/toolkit';

import type { AutosAdvogadoState } from '../types';
import {
  carregarAndamentosAdvogado,
  criarSolicitacaoAdvogado,
  carregarAutosAdvogado,
  carregarChatAdvogado,
  carregarDocumentosAdvogado,
  carregarPendenciasAdvogado,
  carregarSolicitacoesAdvogado,
  enviarMensagemAdvogado,
  marcarMensagemLidaAdvogado,
  registrarAndamentoAdvogado,
  responderPendenciaAdvogado,
} from './autosActions';

const initialState: AutosAdvogadoState = {
  processoAtivo: null,
  autos: null,
  autosStatus: 'idle',
  autosErro: null,
  andamentos: [],
  andamentosStatus: 'idle',
  submittingAndamento: false,
  submittingSolicitacao: false,
  documentos: [],
  documentosStatus: 'idle',
  pendencias: [],
  pendenciasStatus: 'idle',
  submittingPendenciaId: null,
  mensagensChat: [],
  chatStatus: 'idle',
  enviandoMensagem: false,
  solicitacoes: [],
};

const autosAdvogadoSlice = createSlice({
  name: 'advogadoAutos',
  initialState,
  reducers: {
    limparAutosAdvogado: () => initialState,
    definirProcessoAtivo: (state, action: PayloadAction<string>) => {
      if (state.processoAtivo === action.payload) return;
      return { ...initialState, processoAtivo: action.payload };
    },
  },
  extraReducers: (builder) => {
    const doProcessoAtivo = (
      state: AutosAdvogadoState,
      action: { meta: { arg: string | { processoId: string } } },
    ): boolean => {
      const arg = action.meta.arg;
      const processoId = typeof arg === 'string' ? arg : arg.processoId;
      return state.processoAtivo === processoId;
    };

    builder
      .addCase(carregarAutosAdvogado.pending, (state) => {
        state.autosStatus = 'loading';
        state.autosErro = null;
      })
      .addCase(carregarAutosAdvogado.fulfilled, (state, action) => {
        if (!doProcessoAtivo(state, action)) return;
        state.autosStatus = 'succeeded';
        state.autos = action.payload;
      })
      .addCase(carregarAutosAdvogado.rejected, (state, action) => {
        state.autosStatus = 'failed';
        state.autosErro = action.payload ?? 'Não foi possível carregar o processo.';
      })

      .addCase(carregarAndamentosAdvogado.pending, (state) => {
        state.andamentosStatus = 'loading';
      })
      .addCase(carregarAndamentosAdvogado.fulfilled, (state, action) => {
        if (!doProcessoAtivo(state, action)) return;
        state.andamentosStatus = 'succeeded';
        state.andamentos = action.payload;
      })
      .addCase(carregarAndamentosAdvogado.rejected, (state) => {
        state.andamentosStatus = 'failed';
      })

      .addCase(criarSolicitacaoAdvogado.pending, (state) => {
        state.submittingSolicitacao = true;
      })
      .addCase(criarSolicitacaoAdvogado.fulfilled, (state, action) => {
        if (!doProcessoAtivo(state, action)) return;
        state.submittingSolicitacao = false;
        state.solicitacoes = [action.payload, ...state.solicitacoes];
      })
      .addCase(criarSolicitacaoAdvogado.rejected, (state, action) => {
        // O lock também é por processo: soltar o de B porque a resposta de A falhou devolve
        // o duplo clique que o `SolicitacaoDrawerShell` fecha. Trocar de processo já zera o
        // estado, então nada fica preso.
        if (!doProcessoAtivo(state, action)) return;
        state.submittingSolicitacao = false;
      })
      .addCase(registrarAndamentoAdvogado.pending, (state) => {
        state.submittingAndamento = true;
      })
      .addCase(registrarAndamentoAdvogado.fulfilled, (state, action) => {
        if (!doProcessoAtivo(state, action)) return;
        state.submittingAndamento = false;
        state.andamentos = [action.payload, ...state.andamentos];
      })
      .addCase(registrarAndamentoAdvogado.rejected, (state) => {
        state.submittingAndamento = false;
      })

      .addCase(carregarDocumentosAdvogado.pending, (state) => {
        state.documentosStatus = 'loading';
      })
      .addCase(carregarDocumentosAdvogado.fulfilled, (state, action) => {
        if (!doProcessoAtivo(state, action)) return;
        state.documentosStatus = 'succeeded';
        state.documentos = action.payload;
      })
      .addCase(carregarDocumentosAdvogado.rejected, (state) => {
        state.documentosStatus = 'failed';
      })

      .addCase(carregarPendenciasAdvogado.pending, (state) => {
        state.pendenciasStatus = 'loading';
      })
      .addCase(carregarPendenciasAdvogado.fulfilled, (state, action) => {
        if (!doProcessoAtivo(state, action)) return;
        state.pendenciasStatus = 'succeeded';
        state.pendencias = action.payload;
      })
      .addCase(carregarPendenciasAdvogado.rejected, (state) => {
        state.pendenciasStatus = 'failed';
      })

      .addCase(responderPendenciaAdvogado.pending, (state, action) => {
        state.submittingPendenciaId = action.meta.arg.pendenciaId;
      })
      .addCase(responderPendenciaAdvogado.fulfilled, (state, action) => {
        if (!doProcessoAtivo(state, action)) return;
        state.submittingPendenciaId = null;
        if (action.payload) state.pendencias = action.payload;
      })
      .addCase(responderPendenciaAdvogado.rejected, (state) => {
        state.submittingPendenciaId = null;
      })

      .addCase(carregarChatAdvogado.pending, (state) => {
        state.chatStatus = 'loading';
      })
      .addCase(carregarChatAdvogado.fulfilled, (state, action) => {
        if (!doProcessoAtivo(state, action)) return;
        state.chatStatus = 'succeeded';
        state.mensagensChat = action.payload;
      })
      .addCase(carregarChatAdvogado.rejected, (state) => {
        state.chatStatus = 'failed';
      })

      .addCase(enviarMensagemAdvogado.pending, (state) => {
        state.enviandoMensagem = true;
      })
      .addCase(enviarMensagemAdvogado.fulfilled, (state, action) => {
        if (!doProcessoAtivo(state, action)) return;
        state.enviandoMensagem = false;
        state.mensagensChat = [...state.mensagensChat, action.payload];
      })
      .addCase(enviarMensagemAdvogado.rejected, (state) => {
        state.enviandoMensagem = false;
      })

      .addCase(marcarMensagemLidaAdvogado.fulfilled, (state, action) => {
        if (!doProcessoAtivo(state, action)) return;
        state.mensagensChat = state.mensagensChat.map((mensagem) =>
          mensagem.id === action.payload ? { ...mensagem, lida: true } : mensagem,
        );
      })

      .addCase(carregarSolicitacoesAdvogado.fulfilled, (state, action) => {
        if (!doProcessoAtivo(state, action)) return;
        state.solicitacoes = action.payload;
      });
  },
});

export const { definirProcessoAtivo, limparAutosAdvogado } = autosAdvogadoSlice.actions;
export const autosAdvogadoReducer = autosAdvogadoSlice.reducer;
